import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaAndroid,
  FaShieldAlt,
  FaUsers,
  FaBolt,
  FaFileAlt,
  FaArrowRight,
  FaLayerGroup,
  FaBrain,
} from 'react-icons/fa';
import logoImage from '../assets/logo.png';

/* ─────────────────────────────────────────────────────────────
   DATA
───────────────────────────────────────────────────────────── */
const productLinks = [
  { name: 'Trend Genie',          desc: 'Real-time AI crypto signals with TP & stop loss', to: '/products/android-app',  icon: FaAndroid,   accent: '#29ABE2', accentBg: 'rgba(41,171,226,0.10)',  status: 'Live' },
  { name: 'RugShield',            desc: 'DeFi token scanner & honeypot checker',           to: '/rugshield-defi-scanner', icon: FaShieldAlt, accent: '#10b981', accentBg: 'rgba(16,185,129,0.10)',  status: 'Live' },
  { name: 'GenieSocial',   desc: 'Crypto community + earn $TGEN rewards',           to: '/products/social',        icon: FaUsers,     accent: '#8B5CF6', accentBg: 'rgba(139,92,246,0.10)',  status: 'Live' },
  { name: 'GenieNova',                 desc: 'Advanced AI intelligence platform',               to: '/products/nova',          icon: FaBolt,      accent: '#F7941D', accentBg: 'rgba(247,148,29,0.10)',  status: 'Testing' },
  { name: '$TGEN Token',          desc: 'Native ecosystem token with utility and live stats', to: '/tgen-token',            icon: FaBrain,     accent: '#ec4899', accentBg: 'rgba(236,72,153,0.10)', status: 'Live' },
];

const insightLinks = [
  { name: 'Insights Hub', desc: 'Trading guides, AI signal strategies, and market education', to: '/insights', icon: FaBrain, accent: '#29ABE2', accentBg: 'rgba(41,171,226,0.10)', status: 'Hub' },
  { name: 'White Paper', desc: 'Read the Trend Genie whitepaper and ecosystem overview', to: '/whitepaper', icon: FaFileAlt, accent: '#F7941D', accentBg: 'rgba(247,148,29,0.10)', status: 'Read' },
];

const navLinks = [
  { name: 'Home',        to: '/' },
  { name: 'About Us',   to: '/about' },
  { name: 'News',   to: '/news' },
  { name: 'Live Results', to: '/live-results' },
];

/* ─────────────────────────────────────────────────────────────
   KEYFRAMES
───────────────────────────────────────────────────────────── */
const borderFlow = keyframes`
  0%   { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
`;

const aiPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(41,171,226,0.7); opacity: 1; }
  50%       { box-shadow: 0 0 0 7px rgba(41,171,226,0); opacity: 0.7; }
`;

const logoBreathe = keyframes`
  0%, 100% { filter: drop-shadow(0 0 6px rgba(41,171,226,0.4)) drop-shadow(0 2px 8px rgba(41,171,226,0.2)); }
  50%       { filter: drop-shadow(0 0 14px rgba(41,171,226,0.7)) drop-shadow(0 0 30px rgba(247,148,29,0.2)); }
`;

const shimmer = keyframes`
  0%   { transform: translateX(-200%) skewX(-18deg); }
  100% { transform: translateX(400%)  skewX(-18deg); }
`;

const scanLine = keyframes`
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(500%); }
`;

const livePulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.6); }
  50%       { box-shadow: 0 0 0 4px rgba(16,185,129,0); }
`;

const gradientShift = keyframes`
  0%   { background-position: 0%   50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0%   50%; }
`;

const floatBadge = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-2px); }
`;

/* ─────────────────────────────────────────────────────────────
   FRAMER VARIANTS
───────────────────────────────────────────────────────────── */
const panelVariants = {
  hidden: {
    opacity: 0,
    y: -18,
    scale: 0.92,
    rotateX: -14,
    filter: 'blur(6px)',
    transformPerspective: 900,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    rotateX: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.38,
      ease: [0.22, 1, 0.36, 1],
      opacity: { duration: 0.22 },
      filter: { duration: 0.28 },
    },
  },
  exit: {
    opacity: 0,
    y: -10,
    scale: 0.95,
    rotateX: -8,
    filter: 'blur(4px)',
    transition: { duration: 0.2, ease: 'easeIn' },
  },
};

const itemVariants = {
  hidden:  { opacity: 0, x: -8 },
  visible: (i) => ({ opacity: 1, x: 0, transition: { duration: 0.22, delay: i * 0.05, ease: [0.22,1,0.36,1] } }),
};

const linkHoverVariants = {
  rest:  { opacity: 0, scale: 0.85 },
  hover: { opacity: 1, scale: 1,   transition: { duration: 0.18 } },
};

/* ─────────────────────────────────────────────────────────────
   NAVBAR SHELL
───────────────────────────────────────────────────────────── */
const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 2000;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${p => p.$scrolled ? '0.55rem 8%' : '0.95rem 8%'};
  transition: padding 0.3s ease, background 0.35s ease, box-shadow 0.3s ease;

  /* glass background */
  background: ${p => p.$scrolled
    ? 'rgba(255, 255, 255, 0.96)'
    : 'rgba(255, 255, 255, 0.80)'};
  backdrop-filter: blur(22px);
  -webkit-backdrop-filter: blur(22px);
  box-shadow: ${p => p.$scrolled
    ? '0 2px 24px rgba(13,34,53,0.09)'
    : 'none'};

  /* top separator */
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: ${p => p.$scrolled
      ? 'rgba(13,34,53,0.10)'
      : 'rgba(13,34,53,0.06)'};
    transition: background 0.35s;
  }

  @media (max-width: 768px) { padding: 0.75rem 5%; }
`;

/* Animated gradient line at the bottom of the navbar */
const NavBorderLine = styled.div`
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 1.5px;
  background: linear-gradient(90deg,
    transparent 0%,
    #29ABE2 20%,
    #8B5CF6 40%,
    #F7941D 60%,
    #10b981 80%,
    transparent 100%
  );
  background-size: 200% 100%;
  animation: ${borderFlow} 4s linear infinite;
  opacity: ${p => p.$scrolled ? 0.7 : 0.35};
  transition: opacity 0.35s;
`;

/* ─── LOGO ──────────────────────────────────────────────────── */
const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  text-decoration: none;
  position: relative;
  z-index: 1;
`;

const LogoGlowRing = styled.div`
  position: absolute;
  inset: -4px;
  border-radius: 18px;
  background: radial-gradient(circle, rgba(41,171,226,0.25) 0%, transparent 70%);
  opacity: 0;
  transition: opacity 0.3s;
  pointer-events: none;

  ${LogoContainer}:hover & { opacity: 1; }
`;

const LogoImage = styled.img`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  animation: ${logoBreathe} 3.5s ease-in-out infinite;
  position: relative;
  z-index: 1;
`;

const LogoText = styled.span`
  font-size: 1.3rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  background: linear-gradient(135deg, #0d2235 0%, #29ABE2 55%, #F7941D 100%);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${gradientShift} 5s ease infinite;

  @media (max-width: 768px) { display: none; }
`;

/* AI live badge next to logo */
const AiBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  background: rgba(41,171,226,0.12);
  border: 1px solid rgba(41,171,226,0.3);
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #29ABE2;
  text-transform: uppercase;
  animation: ${floatBadge} 2.8s ease-in-out infinite;

  @media (max-width: 900px) { display: none; }
`;

const AiDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #29ABE2;
  animation: ${aiPulse} 1.6s ease-in-out infinite;
`;

/* ─── NAV LINKS ─────────────────────────────────────────────── */
const NavLinks = styled.div`
  display: flex;
  gap: 0.25rem;
  align-items: center;

  @media (max-width: 1024px) { display: none; }
`;

const NavItem = styled.div`
  position: relative;
`;

const NavLinkInner = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.75rem;
  border-radius: 9px;
  font-size: 0.88rem;
  font-weight: 500;
  text-decoration: none;
  color: ${p => p.$active ? '#0d2235' : 'rgba(13,34,53,0.62)'};
  transition: color 0.22s;
  position: relative;
  z-index: 1;
  white-space: nowrap;

  &:hover { color: #0d2235; }
`;

/* Floating highlight pill behind active / hovered links */
const LinkHighlight = styled(motion.div)`
  position: absolute;
  inset: 0;
  border-radius: 9px;
  background: ${p => p.$active
    ? 'rgba(41,171,226,0.10)'
    : 'rgba(13,34,53,0.05)'};
  border: 1px solid ${p => p.$active
    ? 'rgba(41,171,226,0.25)'
    : 'rgba(13,34,53,0.07)'};
  pointer-events: none;
`;

/* Active dot indicator below active link */
const ActiveDot = styled(motion.div)`
  position: absolute;
  bottom: -2px;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #29ABE2;
  box-shadow: 0 0 6px rgba(41,171,226,0.9);
`;

/* ─── PRODUCTS TRIGGER ──────────────────────────────────────── */
const ProductsTrigger = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.45rem 0.75rem;
  border-radius: 9px;
  font-size: 0.88rem;
  font-weight: 500;
  text-decoration: none;
  color: ${p => p.$active ? '#0d2235' : 'rgba(13,34,53,0.62)'};
  transition: color 0.22s;
  position: relative;
  z-index: 1;
  white-space: nowrap;

  &:hover { color: #0d2235; }
`;

const ChevronIcon = styled(motion.span)`
  font-size: 0.58rem;
  display: inline-flex;
  align-items: center;
  color: inherit;
`;

const DropdownWrap = styled.div`
  position: relative;
`;

const DropdownOuter = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  padding-top: 12px;
  z-index: 3000;
`;

/* ─── DROPDOWN PANEL ────────────────────────────────────────── */
const DropdownPanel = styled(motion.div)`
  width: 480px;
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid rgba(13,34,53,0.10);
  border-radius: 22px;
  box-shadow:
    0 20px 60px rgba(13,34,53,0.14),
    0 4px 16px rgba(13,34,53,0.06);
  overflow: hidden;
`;

const PanelHeader = styled.div`
  padding: 1.15rem 1.35rem 1rem;
  background: linear-gradient(135deg, rgba(240,248,255,0.95) 0%, rgba(248,252,255,0.95) 100%);
  border-bottom: 1px solid rgba(13,34,53,0.08);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(13,34,53,0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(13,34,53,0.04) 1px, transparent 1px);
    background-size: 28px 28px;
    mask-image: radial-gradient(ellipse 90% 90% at 50% 50%, black 20%, transparent 75%);
  }
`;

const PanelScanBar = styled.div`
  position: absolute;
  top: 0; left: 0;
  width: 80px; height: 100%;
  background: linear-gradient(90deg, transparent, rgba(41,171,226,0.15), transparent);
  animation: ${scanLine} 3.5s ease-in-out infinite;
`;

const PanelHeaderInner = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PanelKicker = styled.p`
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(41,171,226,0.85);
  margin: 0 0 0.2rem;
`;

const PanelTitle = styled.p`
  font-size: 0.95rem;
  font-weight: 700;
  color: #0d2235;
  margin: 0;
`;

const PanelBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.38rem;
  padding: 0.28rem 0.75rem;
  border-radius: 999px;
  background: rgba(41,171,226,0.12);
  border: 1px solid rgba(41,171,226,0.28);
  font-size: 0.66rem;
  font-weight: 700;
  color: #29ABE2;
  letter-spacing: 0.06em;
`;

const LiveDot = styled.span`
  width: 6px; height: 6px;
  border-radius: 50%;
  background: #10b981;
  display: inline-block;
  animation: ${livePulse} 1.8s ease-in-out infinite;
`;

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.45rem;
  padding: 0.85rem;
`;

const ProductItem = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  gap: 0.72rem;
  padding: 0.85rem 0.9rem;
  border-radius: 14px;
  text-decoration: none;
  border: 1px solid transparent;
  transition: background 0.18s, border-color 0.18s;
  cursor: pointer;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0;
    width: 3px; height: 100%;
    background: ${p => p.$accent};
    opacity: 0;
    transition: opacity 0.18s;
    border-radius: 0 2px 2px 0;
  }

  &:hover {
    background: ${p => p.$accentBg};
    border-color: ${p => p.$accent}28;
  }

  &:hover::before { opacity: 1; }
  &:hover .item-icon { transform: scale(1.14) rotate(-5deg); }
`;

const ItemIconWrap = styled.div`
  width: 38px; height: 38px;
  border-radius: 11px;
  background: ${p => p.$accentBg};
  border: 1px solid ${p => p.$accent}30;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.$accent};
  font-size: 1rem;
  flex-shrink: 0;
  transition: transform 0.24s cubic-bezier(0.34,1.56,0.64,1);
`;

const ItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemName = styled.p`
  font-size: 0.87rem;
  font-weight: 700;
  color: #0d2235;
  margin: 0 0 0.2rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemDesc = styled.p`
  font-size: 0.73rem;
  color: rgba(13,34,53,0.52);
  margin: 0 0 0.35rem;
  line-height: 1.5;
`;

const ItemStatus = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.28rem;
  padding: 0.14rem 0.48rem;
  border-radius: 999px;
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: ${p => p.$live ? 'rgba(16,185,129,0.12)' : 'rgba(247,148,29,0.1)'};
  color: ${p => p.$live ? '#10b981' : '#f7941d'};
  border: 1px solid ${p => p.$live ? 'rgba(16,185,129,0.25)' : 'rgba(247,148,29,0.22)'};

  &::before {
    content: '';
    width: 4px; height: 4px;
    border-radius: 50%;
    background: currentColor;
    display: ${p => p.$live ? 'block' : 'none'};
  }
`;

const PanelFooter = styled.div`
  border-top: 1px solid rgba(13,34,53,0.08);
  padding: 0.65rem 0.85rem;
  background: rgba(240,248,255,0.70);
`;

const ViewAllLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.65rem 0.9rem;
  border-radius: 13px;
  text-decoration: none;
  background: linear-gradient(135deg, rgba(41,171,226,0.1) 0%, rgba(139,92,246,0.06) 100%);
  border: 1px solid rgba(41,171,226,0.18);
  transition: background 0.2s, border-color 0.2s, transform 0.18s;

  &:hover {
    background: linear-gradient(135deg, rgba(41,171,226,0.18) 0%, rgba(139,92,246,0.1) 100%);
    border-color: rgba(41,171,226,0.4);
    transform: translateY(-1px);
  }
`;

const ViewAllLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
`;

const ViewAllIconWrap = styled.div`
  width: 30px; height: 30px;
  border-radius: 9px;
  background: rgba(41,171,226,0.14);
  border: 1px solid rgba(41,171,226,0.28);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #29ABE2;
  font-size: 0.82rem;
`;

const ViewAllText = styled.span`
  font-size: 0.86rem;
  font-weight: 700;
  color: #0d2235;
`;

const ViewAllSub = styled.span`
  font-size: 0.71rem;
  color: rgba(13,34,53,0.48);
  margin-left: 0.5rem;
`;

const ViewAllArrow = styled.span`
  color: #29ABE2;
  font-size: 0.78rem;
  display: flex;
  align-items: center;
`;

/* ─── CTA BUTTON ────────────────────────────────────────────── */
const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.35rem;
  border-radius: 11px;
  font-weight: 700;
  font-size: 0.88rem;
  text-decoration: none;
  color: white !important;
  -webkit-text-fill-color: white !important;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #29ABE2 0%, #1a7fad 50%, #8B5CF6 100%);
  background-size: 200% 200%;
  animation: ${gradientShift} 4s ease infinite;
  box-shadow: 0 4px 18px rgba(41,171,226,0.35);
  transition: transform 0.2s, box-shadow 0.2s;

  &::after {
    content: '';
    position: absolute;
    top: 0; left: 0;
    width: 50px; height: 100%;
    background: rgba(255,255,255,0.28);
    filter: blur(10px);
    transform: translateX(-200%) skewX(-18deg);
  }

  &:hover {
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 8px 28px rgba(41,171,226,0.55);

    &::after {
      animation: ${shimmer} 0.55s ease-in-out forwards;
    }
  }

  @media (max-width: 1024px) { display: none; }
`;

/* ─── HAMBURGER ─────────────────────────────────────────────── */
const Hamburger = styled.div`
  width: 26px;
  height: 20px;
  position: relative;
  cursor: pointer;
  display: none;
  z-index: 1;

  @media (max-width: 1024px) { display: block; }

  div {
    position: absolute;
    height: 2px;
    background: rgba(13,34,53,0.75);
    border-radius: 4px;
    transition: 0.38s ease;
  }

  div:nth-child(1) {
    top: ${p => p.$open ? '9px' : '0'};
    width: ${p => p.$open ? '100%' : '80%'};
    transform: ${p => p.$open ? 'rotate(45deg)' : 'none'};
  }
  div:nth-child(2) {
    width: 100%;
    top: 9px;
    opacity: ${p => p.$open ? '0' : '1'};
    transform: ${p => p.$open ? 'scaleX(0)' : 'scaleX(1)'};
  }
  div:nth-child(3) {
    bottom: ${p => p.$open ? '9px' : '0'};
    width: ${p => p.$open ? '100%' : '60%'};
    transform: ${p => p.$open ? 'rotate(-45deg)' : 'none'};
  }
`;

/* ─── MOBILE MENU ───────────────────────────────────────────── */
const MobileOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(13, 34, 53, 0.30);
  backdrop-filter: blur(4px);
  z-index: 2400;
`;

const MobileMenuWrapper = styled(motion.div)`
  position: fixed;
  top: 0; right: 0;
  width: 82%;
  max-width: 360px;
  height: 100vh;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(24px);
  padding: 0;
  z-index: 2500;
  border-left: 1px solid rgba(13,34,53,0.10);
  overflow-y: auto;
  box-shadow: -16px 0 60px rgba(13,34,53,0.14);
`;

const MobileMenuTop = styled.div`
  padding: 1.4rem 1.4rem 1rem;
  border-bottom: 1px solid rgba(13,34,53,0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, rgba(240,248,255,0.95) 0%, rgba(248,252,255,0.95) 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(13,34,53,0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(13,34,53,0.04) 1px, transparent 1px);
    background-size: 24px 24px;
    mask-image: radial-gradient(ellipse at center, black 10%, transparent 75%);
  }
`;

const MobileMenuClose = styled.button`
  background: rgba(13,34,53,0.06);
  border: 1px solid rgba(13,34,53,0.12);
  border-radius: 9px;
  width: 34px; height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1rem;
  color: rgba(13,34,53,0.60);
  transition: all 0.2s;
  position: relative;
  z-index: 1;

  &:hover {
    background: rgba(13,34,53,0.10);
    color: #0d2235;
  }
`;

const MobileMenuLinks = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem 0.85rem 1.5rem;
  gap: 0.15rem;
`;

const MobileGroupLabel = styled.div`
  padding: 1rem 0.75rem 0.35rem;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(13,34,53,0.42);
`;

const MobileNavLink = styled(Link)`
  color: rgba(13,34,53,0.72);
  font-size: 0.96rem;
  text-decoration: none;
  font-weight: 500;
  padding: 0.72rem 0.9rem;
  border-radius: 11px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    color: #0d2235;
    background: rgba(41,171,226,0.08);
  }
`;

const MobileSubLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  color: rgba(13,34,53,0.68);
  font-size: 0.92rem;
  text-decoration: none;
  font-weight: 500;
  padding: 0.65rem 0.9rem;
  border-radius: 11px;
  transition: all 0.2s;

  &:hover {
    color: #0d2235;
    background: rgba(13,34,53,0.05);
  }
`;

const MobileItemIcon = styled.span`
  width: 30px; height: 30px;
  border-radius: 9px;
  background: ${p => p.$accentBg};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.$accent};
  font-size: 0.82rem;
  flex-shrink: 0;
`;

const MobileCTA = styled.a`
  margin: 0.5rem 0.85rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #29abe2, #8B5CF6);
  background-size: 200% 200%;
  animation: ${gradientShift} 4s ease infinite;
  padding: 0.9rem 1.6rem;
  border-radius: 13px;
  color: white !important;
  -webkit-text-fill-color: white !important;
  font-weight: 700;
  text-decoration: none;
  font-size: 0.96rem;
  box-shadow: 0 6px 22px rgba(41,171,226,0.3);
`;

/* ─────────────────────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────────────────────── */
const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled]     = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [hoveredLink, setHoveredLink]   = useState(null);
  const location = useLocation();
  const productsActive = location.pathname.startsWith('/products') || location.pathname === '/tgen-token' || location.pathname === '/token';
  const insightsActive = location.pathname.startsWith('/insights') || location.pathname === '/whitepaper';

  useEffect(() => {
    setMobileOpen(false);
    setProductsOpen(false);
    setInsightsOpen(false);
  }, [location]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <Nav $scrolled={scrolled}>
        <NavBorderLine $scrolled={scrolled} />

        {/* ── LOGO ── */}
        <LogoContainer to="/">
          <LogoGlowRing />
          <LogoImage src={logoImage} alt="Trend Genie AI Crypto Signals App" />
          <LogoText>Trend Genie</LogoText>
          <AiBadge><AiDot />AI Live</AiBadge>
        </LogoContainer>

        {/* ── DESKTOP LINKS ── */}
        <NavLinks>
          {navLinks.slice(0, 3).map((link) => {
            const isActive = location.pathname === link.to;
            return (
              <NavItem
                key={link.to}
                onMouseEnter={() => setHoveredLink(link.to)}
                onMouseLeave={() => setHoveredLink(null)}
              >
                <AnimatePresence>
                  {(hoveredLink === link.to || isActive) && (
                    <LinkHighlight
                      key="hl"
                      $active={isActive}
                      initial={{ opacity: 0, scale: 0.88 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.88 }}
                      transition={{ duration: 0.16 }}
                    />
                  )}
                </AnimatePresence>
                <NavLinkInner to={link.to} $active={isActive ? 1 : 0}>
                  {link.name}
                </NavLinkInner>
                {isActive && (
                  <ActiveDot
                    layoutId="activeNavDot"
                    transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                  />
                )}
              </NavItem>
            );
          })}

          {/* Products dropdown */}
          <DropdownWrap
            onMouseEnter={() => setProductsOpen(true)}
            onMouseLeave={() => setProductsOpen(false)}
          >
            <NavItem
              onMouseEnter={() => setHoveredLink('products')}
              onMouseLeave={() => setHoveredLink(null)}
            >
              <AnimatePresence>
                {(hoveredLink === 'products' || productsActive) && (
                  <LinkHighlight
                    key="hl-p"
                    $active={productsActive}
                    initial={{ opacity: 0, scale: 0.88 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.88 }}
                    transition={{ duration: 0.16 }}
                  />
                )}
              </AnimatePresence>
              <ProductsTrigger
                to="/products"
                $active={productsActive ? 1 : 0}
                onClick={() => setProductsOpen(false)}
              >
                Products
                <ChevronIcon
                  animate={{ rotate: productsOpen ? 180 : 0 }}
                  transition={{ duration: 0.22 }}
                >
                  ▾
                </ChevronIcon>
              </ProductsTrigger>
              {productsActive && (
                <ActiveDot
                  layoutId="activeNavDot"
                  transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                />
              )}
            </NavItem>

            <DropdownOuter>
              <AnimatePresence>
                {productsOpen && (
                  <DropdownPanel
                    key="panel"
                    variants={panelVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    <PanelHeader>
                      <PanelScanBar />
                      <PanelHeaderInner>
                        <div>
                          <PanelKicker>Product Ecosystem</PanelKicker>
                          <PanelTitle>Five Connected Products</PanelTitle>
                        </div>
                        <PanelBadge><LiveDot /> Trend Genie</PanelBadge>
                      </PanelHeaderInner>
                    </PanelHeader>

                    <ItemsGrid>
                      {productLinks.map((product, i) => {
                        const Icon = product.icon;
                        return (
                          <ProductItem
                            key={product.to}
                            as={Link}
                            to={product.to}
                            $accent={product.accent}
                            $accentBg={product.accentBg}
                            custom={i}
                            variants={itemVariants}
                            initial="hidden"
                            animate="visible"
                            onClick={() => setProductsOpen(false)}
                          >
                            <ItemIconWrap
                              className="item-icon"
                              $accent={product.accent}
                              $accentBg={product.accentBg}
                            >
                              <Icon />
                            </ItemIconWrap>
                            <ItemInfo>
                              <ItemName>{product.name}</ItemName>
                              <ItemDesc>{product.desc}</ItemDesc>
                              <ItemStatus $live={product.status === 'Live'}>
                                {product.status}
                              </ItemStatus>
                            </ItemInfo>
                          </ProductItem>
                        );
                      })}
                    </ItemsGrid>

                    <PanelFooter>
                      <ViewAllLink to="/products" onClick={() => setProductsOpen(false)}>
                        <ViewAllLeft>
                          <ViewAllIconWrap><FaLayerGroup /></ViewAllIconWrap>
                          <span>
                            <ViewAllText>Browse All Products</ViewAllText>
                            <ViewAllSub>Overview + full details</ViewAllSub>
                          </span>
                        </ViewAllLeft>
                        <ViewAllArrow><FaArrowRight /></ViewAllArrow>
                      </ViewAllLink>
                    </PanelFooter>
                  </DropdownPanel>
                )}
              </AnimatePresence>
            </DropdownOuter>
          </DropdownWrap>

          <DropdownWrap
            onMouseEnter={() => setInsightsOpen(true)}
            onMouseLeave={() => setInsightsOpen(false)}
          >
            <NavItem
              onMouseEnter={() => setHoveredLink('insights')}
              onMouseLeave={() => setHoveredLink(null)}
            >
              <AnimatePresence>
                {(hoveredLink === 'insights' || insightsActive) && (
                  <LinkHighlight
                    key="hl-i"
                    $active={insightsActive}
                    initial={{ opacity: 0, scale: 0.88 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.88 }}
                    transition={{ duration: 0.16 }}
                  />
                )}
              </AnimatePresence>
              <ProductsTrigger
                to="/insights"
                $active={insightsActive ? 1 : 0}
                onClick={() => setInsightsOpen(false)}
              >
                Insights
                <ChevronIcon
                  animate={{ rotate: insightsOpen ? 180 : 0 }}
                  transition={{ duration: 0.22 }}
                >
                  ▾
                </ChevronIcon>
              </ProductsTrigger>
              {insightsActive && (
                <ActiveDot
                  layoutId="activeNavDot"
                  transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                />
              )}
            </NavItem>

            <DropdownOuter>
              <AnimatePresence>
                {insightsOpen && (
                  <DropdownPanel
                    key="insights-panel"
                    variants={panelVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    <PanelHeader>
                      <PanelScanBar />
                      <PanelHeaderInner>
                        <div>
                          <PanelKicker>Insights & Research</PanelKicker>
                          <PanelTitle>Guides, strategy, and whitepaper</PanelTitle>
                        </div>
                        <PanelBadge><LiveDot /> Research Hub</PanelBadge>
                      </PanelHeaderInner>
                    </PanelHeader>

                    <ItemsGrid>
                      {insightLinks.map((item, i) => {
                        const Icon = item.icon;
                        return (
                          <ProductItem
                            key={item.to}
                            as={Link}
                            to={item.to}
                            $accent={item.accent}
                            $accentBg={item.accentBg}
                            custom={i}
                            variants={itemVariants}
                            initial="hidden"
                            animate="visible"
                            onClick={() => setInsightsOpen(false)}
                          >
                            <ItemIconWrap
                              className="item-icon"
                              $accent={item.accent}
                              $accentBg={item.accentBg}
                            >
                              <Icon />
                            </ItemIconWrap>
                            <ItemInfo>
                              <ItemName>{item.name}</ItemName>
                              <ItemDesc>{item.desc}</ItemDesc>
                              <ItemStatus>{item.status}</ItemStatus>
                            </ItemInfo>
                          </ProductItem>
                        );
                      })}
                    </ItemsGrid>
                  </DropdownPanel>
                )}
              </AnimatePresence>
            </DropdownOuter>
          </DropdownWrap>

          {navLinks.slice(3).map((link) => {
            const isActive = location.pathname === link.to;
            return (
              <NavItem
                key={link.to}
                onMouseEnter={() => setHoveredLink(link.to)}
                onMouseLeave={() => setHoveredLink(null)}
              >
                <AnimatePresence>
                  {(hoveredLink === link.to || isActive) && (
                    <LinkHighlight
                      key="hl"
                      $active={isActive}
                      initial={{ opacity: 0, scale: 0.88 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.88 }}
                      transition={{ duration: 0.16 }}
                    />
                  )}
                </AnimatePresence>
                <NavLinkInner to={link.to} $active={isActive ? 1 : 0}>
                  {link.name}
                </NavLinkInner>
                {isActive && (
                  <ActiveDot
                    layoutId="activeNavDot"
                    transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                  />
                )}
              </NavItem>
            );
          })}
        </NavLinks>

        {/* ── CTA ── */}
        <CTAButton
          href="https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaAndroid style={{ fontSize: '0.9rem' }} /> Download App
        </CTAButton>

        {/* ── HAMBURGER ── */}
        <Hamburger $open={mobileOpen} onClick={() => setMobileOpen(!mobileOpen)}>
          <div /><div /><div />
        </Hamburger>
      </Nav>

      {/* ── MOBILE OVERLAY + MENU ── */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <MobileOverlay
              key="overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setMobileOpen(false)}
            />
            <MobileMenuWrapper
              key="menu"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 32 }}
            >
              <MobileMenuTop>
                <LogoContainer to="/" style={{ textDecoration: 'none' }}>
                  <LogoImage src={logoImage} alt="Trend Genie" style={{ animation: 'none', filter: 'none' }} />
                  <LogoText style={{ display: 'block' }}>Trend Genie</LogoText>
                </LogoContainer>
                <MobileMenuClose onClick={() => setMobileOpen(false)}>✕</MobileMenuClose>
              </MobileMenuTop>

              <MobileMenuLinks>
                <MobileNavLink to="/">Home</MobileNavLink>
                <MobileNavLink to="/about">About Us</MobileNavLink>

                <MobileGroupLabel>Products</MobileGroupLabel>
                <MobileSubLink to="/products">
                  <MobileItemIcon $accent="#29ABE2" $accentBg="rgba(41,171,226,0.1)">
                    <FaLayerGroup />
                  </MobileItemIcon>
                  Products Overview
                </MobileSubLink>
                {productLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <MobileSubLink key={link.to} to={link.to}>
                      <MobileItemIcon $accent={link.accent} $accentBg={link.accentBg}>
                        <Icon />
                      </MobileItemIcon>
                      {link.name}
                    </MobileSubLink>
                  );
                })}

                <MobileGroupLabel>Insights</MobileGroupLabel>
                <MobileNavLink to="/insights">Insights Hub</MobileNavLink>
                <MobileNavLink to="/whitepaper">White Paper</MobileNavLink>

                <MobileGroupLabel>More</MobileGroupLabel>
                <MobileNavLink to="/news">News</MobileNavLink>
                <MobileNavLink to="/live-results">Live Results</MobileNavLink>
                <MobileNavLink to="/social">Genie Social</MobileNavLink>
                <MobileNavLink to="/tgen-airdrop-live">Rewards</MobileNavLink>
              </MobileMenuLinks>

              <MobileCTA
                href="https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaAndroid /> Download Free App
              </MobileCTA>
            </MobileMenuWrapper>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
